// 保存本文（生 Markdown）の指定範囲を `[表示テキスト](URL)` のリンクへ変換する共有モジュール。
//
// 方針:
// - 「範囲をリンクにする」責務を純粋関数 1 つ（linkifyMarkdownRange）へ集約し、
//   図形選択からの一括リンク化（linkify-selected-objects.ts）と、本文編集中の範囲選択
//   リンク化（use-object-text-editor.ts の onPaste）が同じ実装を参照する。
// - 変換は「純粋な挿入」または「純粋な削除」だけからなる編集列へ分解し、1 件ずつ
//   rebaseTextInlineStyles を通す。置換（削除と挿入の同時実行）を作らないため、
//   部分書式（inlineStyles の run）が表示文字へ付いたまま残る。
// - 記法ユーティリティ（ラベル・URL のエスケープ）は、以前 apps/web の paste-links.ts が
//   内部に持っていたロジックをそのまま移設したものである（web 側は本モジュールを import する）。
// - 本モジュールは純粋関数のみで、DOM・CSS・React へ依存しない。

import {
  buildMarkdownSourceMap,
  clipRangeToMarkdownVisible,
  isHttpUrl,
  markdownLineDisplaySegments,
  splitTextIntoLinkSegments,
  type MarkdownSourceLine,
  type RawOffsetRange,
} from "./markdown-source-map";
import {
  rebaseTextInlineStyles,
  TEXT_STYLE_EXPECTED_TEXT_MAX_LENGTH,
  type TextInlineStyle,
  type TextInlineStyleRange,
} from "./text-inline-style";

//------------------------------------------------------------------------------
// 記法ユーティリティ
//------------------------------------------------------------------------------

/** `[表示テキスト](URL)` のラベル側で記法を壊す文字（\ [ ]）をエスケープする。 */
export function escapeMarkdownLinkLabel(text: string): string {
  return text.replace(/([\\[\]])/gu, "\\$1");
}

/**
 * URL をそのまま `[x](URL)` の URL 部へ埋め込んでも、表示側 parser（splitTextIntoLinkSegments）が
 * 同一の URL として往復できるか。均衡した括弧（例: /path_(v2)）は現行 parser がそのまま
 * 解釈できるためエスケープ不要（( ) は reserved character のため、不要な %28/%29 化は
 * サーバによっては別リソース扱いになり得る）。
 */
function canRoundTripAsMarkdownLinkUrl(url: string): boolean {
  const segments = splitTextIntoLinkSegments(`[x](${url})`);
  return (
    segments.length === 1 &&
    segments[0]?.type === "link" &&
    segments[0].value === "x" &&
    segments[0].url === url
  );
}

/**
 * `[表示テキスト](URL)` の URL 側を、表示側 parser（splitTextIntoLinkSegments）が
 * 同一 URL として往復できる形へ整える。往復できる URL はそのまま返し、
 * 往復できない場合（空白・不均衡な括弧）だけ %20 / %28 / %29 へ置き換える。
 */
export function escapeMarkdownLinkUrl(url: string): string {
  return canRoundTripAsMarkdownLinkUrl(url)
    ? url
    : url.replace(/ /gu, "%20").replace(/\(/gu, "%28").replace(/\)/gu, "%29");
}

/** ラベルと URL から `[ラベル](URL)` を組み立てる（両方をエスケープしたうえで連結する）。 */
export function formatMarkdownLink(label: string, url: string): string {
  return `[${escapeMarkdownLinkLabel(label)}](${escapeMarkdownLinkUrl(url)})`;
}

//------------------------------------------------------------------------------
// 範囲リンク化
//------------------------------------------------------------------------------

export type LinkifyMarkdownRangeOutcome =
  /** 変換できた。 */
  | {
      readonly kind: "linkified";
      /** 変換後の本文。 */
      readonly text: string;
      /** 変換後の本文に対応させた部分書式 run（入力 runs が空なら空配列）。 */
      readonly runs: readonly TextInlineStyleRange[];
      /** 変換後の本文における、最後に作ったリンクの直後の raw offset。 */
      readonly caret: number;
      /** 作ったリンクの本数（＝変換した可視区間の数）。 */
      readonly linkCount: number;
    }
  /** 変換は成立するが、結果が入力と同一だった（すでに同じ URL のリンクになっている）。 */
  | { readonly kind: "unchanged" }
  /** 範囲に可視文字が無く、リンクにできる表示テキストが取れない。 */
  | { readonly kind: "no-visible-text" }
  /** 変換すると本文長上限を超える。 */
  | { readonly kind: "too-long" }
  /** URL が http(s) でない、空白を含む、または長すぎる。 */
  | { readonly kind: "invalid-url" };

export type PlainLineLinkPlan =
  /** 入力範囲をそのまま置換するときの文字列。両端から除いた空白も insert に含める。 */
  | {
      readonly kind: "replace";
      readonly start: number;
      readonly end: number;
      readonly insert: string;
    }
  /** 既存リンクを壊さないためには、入力範囲を広げる必要がある。 */
  | { readonly kind: "needs-snap" }
  | { readonly kind: "no-visible-text" }
  | { readonly kind: "too-long" }
  | { readonly kind: "invalid-url" }
  | { readonly kind: "unchanged" }
  | { readonly kind: "multiline" };

/**
 * 元本文の座標系で表した 1 件の編集（純粋な挿入または純粋な削除）。
 *
 * **不変条件**: 生成する RawEdit は必ず「start === end（純粋な挿入）」または
 * 「insert === ""（純粋な削除）」のいずれかである。両方を同時に行う編集（置換）は作らない。
 * これが部分書式（run）保持の根拠で、rebaseTextInlineStyles が求める単一置換差分が
 * 「挿入のみ」または「削除のみ」に退化するため、run の移動が曖昧にならない。
 */
interface RawEdit {
  /** 編集開始 offset。 */
  readonly start: number;
  /** 編集終了 offset（挿入では start と同じ）。 */
  readonly end: number;
  /** 挿入する文字列（削除では空文字列）。 */
  readonly insert: string;
}

/** 1 行の中で確定したリンクセグメントの raw 範囲（表示テキスト付きリンクは記法全体）。 */
interface LineLinkSegment {
  readonly start: number;
  readonly end: number;
  /** 表示テキスト付きリンク（raw を持つ）のときの URL。素 URL では undefined。 */
  readonly markdownUrl: string | undefined;
}

/** リンク化する 1 区間と、その区間が属する行のリンクセグメント一覧。 */
interface LinkifyTarget {
  readonly range: RawOffsetRange;
  readonly segments: readonly LineLinkSegment[];
}

/**
 * 行構造を解釈しない生テキストの範囲を、単一の Markdown リンクへ置換する計画を返す。
 * セクションタイトルなど、編集中は記法をそのまま表示する単一行の編集領域で使う。
 */
export function planPlainLineLinkReplacement(params: {
  readonly text: string;
  readonly start: number;
  readonly end: number;
  readonly url: string;
  readonly maxLength?: number | undefined;
}): PlainLineLinkPlan {
  const { text, url } = params;
  if (!isHttpUrl(url) || /\s/u.test(url)) return { kind: "invalid-url" };

  const start = Math.max(0, Math.min(Math.min(params.start, params.end), text.length));
  const end = Math.max(0, Math.min(Math.max(params.start, params.end), text.length));
  if (/[\r\n]/u.test(text.slice(start, end))) return { kind: "multiline" };

  const trimmed = trimRangeWhitespace(text, { end, start });
  if (trimmed === null) return { kind: "no-visible-text" };

  // 選択が収まる生の 1 行をそのまま分割する。見出し・箇条書きの接頭辞も本文として扱う。
  const lineStart = text.lastIndexOf("\n", Math.max(0, start - 1)) + 1;
  const nextLineBreak = text.indexOf("\n", end);
  const lineEnd = nextLineBreak < 0 ? text.length : nextLineBreak;
  const segments = collectTextLinkSegments(text.slice(lineStart, lineEnd), lineStart);
  const snapped = snapRangeToLinkSegments(trimmed, segments, {
    end: lineEnd,
    start: lineStart,
  });
  if (snapped.start !== trimmed.start || snapped.end !== trimmed.end) {
    return { kind: "needs-snap" };
  }

  const label = displayTextOfRawRange(text, trimmed);
  if (!/\S/u.test(label)) return { kind: "no-visible-text" };

  const insert =
    text.slice(start, trimmed.start) +
    formatMarkdownLink(label, url) +
    text.slice(trimmed.end, end);
  const replacement = text.slice(0, start) + insert + text.slice(end);
  if (replacement.length > (params.maxLength ?? TEXT_STYLE_EXPECTED_TEXT_MAX_LENGTH)) {
    return { kind: "too-long" };
  }
  if (replacement === text) return { kind: "unchanged" };
  return { end, insert, kind: "replace", start };
}

/**
 * 保存本文の raw 範囲 `[start, end)` を `[表示テキスト](URL)` のリンクへ変換する。
 * 範囲が複数行にまたがる場合は行ごとに 1 本のリンクを作る（Markdown リンクは 1 行に閉じるため）。
 */
export function linkifyMarkdownRange(params: {
  /** 変換対象の保存本文（生 Markdown）。 */
  readonly text: string;
  /** 変換したい raw 範囲の開始 offset。 */
  readonly start: number;
  /** 変換したい raw 範囲の終端 offset（半開区間）。start より小さくてもよい（内部で並べ替える）。 */
  readonly end: number;
  /** リンク先 URL。呼び出し側が trim 済みの値を渡す。 */
  readonly url: string;
  /** 現在の部分書式 run（テキスト図形のみ。付箋では undefined）。 */
  readonly runs?: readonly TextInlineStyleRange[] | undefined;
  /** 部分書式の基底書式（run の差分計算に使う。省略時は空オブジェクト）。 */
  readonly base?: TextInlineStyle | undefined;
  /** 変換後の本文長の上限（省略時は TEXT_STYLE_EXPECTED_TEXT_MAX_LENGTH = 8192）。 */
  readonly maxLength?: number | undefined;
}): LinkifyMarkdownRangeOutcome {
  const { end, start, text, url } = params;
  const maxLength = params.maxLength ?? TEXT_STYLE_EXPECTED_TEXT_MAX_LENGTH;

  // 手順 1: URL の検証。記法の URL 部は空白を含められないため、空白入りは受け付けない。
  if (!isHttpUrl(url) || /\s/u.test(url)) {
    return { kind: "invalid-url" };
  }

  const map = buildMarkdownSourceMap(text);
  // 手順 2: 可視区間の切り出し（記法プレフィックスと改行を除いた、行ごとの範囲）。
  const clipped = clipRangeToMarkdownVisible(map, start, end);

  const targets: LinkifyTarget[] = [];
  // clipped と map.lines はどちらも raw offset の昇順で、各区間はちょうど 1 行の可視区間へ
  // 収まる（行は改行で分かたれ重ならない）ため、行の探索は前進のみの走査で足りる。
  let lineCursor = 0;
  for (const range of clipped) {
    while (lineCursor < map.lines.length && (map.lines[lineCursor]?.contentEnd ?? 0) < range.end) {
      lineCursor += 1;
    }
    const line = map.lines[lineCursor];
    if (line === undefined || range.start < line.contentStart) continue;
    const segments = collectLineLinkSegments(line);
    // 手順 3: リンクセグメントへのスナップ（記法・素 URL の途中で切らない）。
    const snapped = snapRangeToLinkSegments(range, segments, {
      end: line.contentEnd,
      start: line.contentStart,
    });
    // 手順 4: 両端の空白の除外（行頭・行末の空白をリンクへ含めない）。
    const trimmed = trimRangeWhitespace(text, snapped);
    if (trimmed === null) continue;
    // 手順 5: 表示テキストの確認（記法だけの範囲・空白だけの範囲を捨てる）。
    if (!hasVisibleDisplayText(line, trimmed)) continue;
    targets.push({ range: trimmed, segments });
  }

  // 手順 6: 対象区間が 0 件なら変換できない。
  if (targets.length === 0) {
    return { kind: "no-visible-text" };
  }

  // 手順 7: 編集列の生成。push 順が手順 9 の安定ソートで同一位置の適用順を決める。
  const edits: RawEdit[] = [];
  const escapedUrl = escapeMarkdownLinkUrl(url);
  for (const target of targets) {
    const segments = target.segments.filter(
      (segment) =>
        segment.markdownUrl !== undefined &&
        segment.start < target.range.end &&
        segment.end > target.range.start
    );
    // 区間の先頭が既存リンクの `[` そのものである場合、その削除（手順 7-1）と手順 7-4 の
    // `[` 挿入は同じ位置の同じ 1 文字なので打ち消し合う。両方を作ると「いったん `[` を
    // 消した中間状態」を経由するため、その状態で行の分類が変わる（例: `[- x](URL)` から
    // `[` を消すと箇条書き行になる）と、記法プレフィックス扱いになった文字の override が
    // pruneOverrideMap で落ちてしまう。最終結果は同じなので、対にして省く。
    let keepsLeadingBracket = false;
    // 手順 7-1: 既存の表示テキスト付きリンクは記法ごと剥がす（ラベルは新しいラベルへ再利用する）。
    for (const segment of segments) {
      const labelRawEnd = segment.end - ((segment.markdownUrl ?? "").length + 3);
      // 想定外の構造では記法を触らない（markdownLineDisplaySegments の防御と同じ方針）。
      if (labelRawEnd <= segment.start + 1) continue;
      edits.push({ end: segment.end, insert: "", start: labelRawEnd });
      if (segment.start === target.range.start) {
        keepsLeadingBracket = true;
        continue;
      }
      edits.push({ end: segment.start + 1, insert: "", start: segment.start });
    }
    // 手順 7-2: 記法を壊す文字のエスケープ。既存リンクの raw 範囲（記法文字を含む丸ごと）は
    // 手順 7-1 の削除対象なので除外する（同じ位置へ削除とエスケープを重ねると本文が壊れる）。
    for (let p = target.range.start; p < target.range.end; p += 1) {
      if (segments.some((segment) => p >= segment.start && p < segment.end)) continue;
      const ch = text[p];
      if (ch === "[" || ch === "]" || ch === "\\") {
        edits.push({ end: p, insert: "\\", start: p });
      }
    }
    // 手順 7-3 / 7-4: リンク記法の終端 → 先頭の順に push する。
    // 同一位置の挿入は「先に適用したものが後ろへ来る」ため、先頭の `[` を最後に push すると
    // 結果として最も外側（前）へ来る。
    edits.push({ end: target.range.end, insert: `](${escapedUrl})`, start: target.range.end });
    // 既存リンクの `[` をそのまま流用した場合（keepsLeadingBracket）は新しく挿入しない。
    if (!keepsLeadingBracket) {
      edits.push({ end: target.range.start, insert: "[", start: target.range.start });
    }
  }

  // 手順 8: 長さの事前判定（本文を作る前に判定する。エスケープで増える分も含まれる）。
  let delta = 0;
  for (const edit of edits) delta += edit.insert.length - (edit.end - edit.start);
  if (text.length + delta > maxLength) {
    return { kind: "too-long" };
  }

  // 手順 9: 編集の適用。start の降順・安定ソートで、後方から順に適用する。
  const sorted = stableSortByStartDescending(edits);
  const base = params.base ?? {};
  let current = text;
  let runsNow: readonly TextInlineStyleRange[] = params.runs ?? [];
  for (const edit of sorted) {
    const next = current.slice(0, edit.start) + edit.insert + current.slice(edit.end);
    // runsNow が空配列のときは rebaseTextInlineStyles が即座に空配列を返すため、
    // 部分書式を持たない付箋でも無駄なコストは発生しない。
    runsNow = rebaseTextInlineStyles({ base, nextText: next, prevText: current, runs: runsNow });
    current = next;
  }

  // 手順 10: キャレットの算出。ソート後の先頭要素は「最も後ろの区間のリンク終端挿入」であり、
  // 適用直後の終端は start + insert.length。その後に適用する編集はすべて前方にあるため、
  // それらの長さ差分の総和を足した位置が最終的なキャレットになる。
  const first = sorted[0];
  let caret = current.length;
  if (first !== undefined) {
    caret = first.start + first.insert.length;
    for (const edit of sorted.slice(1)) caret += edit.insert.length - (edit.end - edit.start);
  }

  // 手順 11: 結果が入力と同一（すでに同じ URL のリンク）なら変換しない。
  if (current === text) {
    return { kind: "unchanged" };
  }

  // 手順 12
  return {
    caret: Math.max(0, Math.min(caret, current.length)),
    kind: "linkified",
    linkCount: targets.length,
    runs: runsNow,
    text: current,
  };
}

/**
 * 1 行の可視本文をリンクセグメントへ分割し、行の raw 座標系での範囲を返す。
 * raw 長は `segment.raw ?? segment.value` で数える（素 URL の link セグメントは raw を
 * 持たず value が URL 文字列なので、raw だけを見ると長さを取り違える）。
 */
function collectLineLinkSegments(line: MarkdownSourceLine): readonly LineLinkSegment[] {
  return collectTextLinkSegments(line.content, line.contentStart);
}

/** 任意の生テキストをリンクセグメントへ分割し、指定した raw offset へ写像する。 */
function collectTextLinkSegments(text: string, offset: number): readonly LineLinkSegment[] {
  const result: LineLinkSegment[] = [];
  let cursor = offset;
  for (const segment of splitTextIntoLinkSegments(text)) {
    const rawLength = (segment.raw ?? segment.value).length;
    if (segment.type === "link") {
      result.push({
        end: cursor + rawLength,
        // raw を持つのは表示テキスト付きリンクだけ。素 URL は undefined のままにする。
        markdownUrl: segment.raw === undefined ? undefined : (segment.url ?? ""),
        start: cursor,
      });
    }
    cursor += rawLength;
  }
  return result;
}

/**
 * 範囲がリンクセグメント（表示テキスト付きリンク・素 URL の両方）と 1 文字でも重なるなら、
 * そのセグメント全体を含むところまで範囲を広げる。広げた結果は行の可視区間を超えない。
 *
 * さらに、範囲の開始が**素 URL の直後**（空白を挟まない）である場合も、その素 URL を含める。
 * 表示側 parser（splitTextIntoLinkSegments → isInsideBareUrlPrefix）は「空白を挟まず素 URL に
 * 続く `[...](` 」を URL の一部と見なして Markdown リンクとして先取りしないため、素 URL の
 * 直後へ `[` を挿入すると新しいリンクが記法として解釈されず、`](URL)` が本文にそのまま
 * 見えてしまう（例: 「詳細はhttps://example.invalid/xを参照」の「を参照」だけを選択した場合）。
 * 素 URL 全体をリンクへ含めれば `[` は URL より前へ入り、記法として正しく解釈される。
 */
function snapRangeToLinkSegments(
  range: RawOffsetRange,
  segments: readonly LineLinkSegment[],
  bounds: RawOffsetRange
): RawOffsetRange {
  let { end, start } = range;
  for (const segment of segments) {
    if (segment.start < end && segment.end > start) {
      start = Math.min(start, segment.start);
      end = Math.max(end, segment.end);
    }
  }
  // 素 URL は 1 つの URL トークンへまとまるため隣接し得ず、この 1 回の後退で十分である
  // （表示テキスト付きリンクの直後は、記法末尾の `)` が URL 候補を不均衡にするため先取りされない）。
  for (const segment of segments) {
    if (segment.markdownUrl === undefined && segment.end === start) {
      start = segment.start;
      break;
    }
  }
  return {
    end: Math.min(end, bounds.end),
    start: Math.max(start, bounds.start),
  };
}

/** raw 範囲を表示したときに現れる文字列を、既存リンク記法を縮約して集める。 */
function displayTextOfRawRange(text: string, range: RawOffsetRange): string {
  return splitTextIntoLinkSegments(text.slice(range.start, range.end))
    .map((segment) => segment.value)
    .join("");
}

/**
 * 範囲の両端の空白を除外する（空になったら null）。
 * 空白の除去はリンク記法の境界（`[` や `)`）に食い込まないため、スナップ結果を壊さない。
 */
function trimRangeWhitespace(text: string, range: RawOffsetRange): RawOffsetRange | null {
  let { end, start } = range;
  while (start < end && /\s/u.test(text[start] ?? "")) start += 1;
  while (end > start && /\s/u.test(text[end - 1] ?? "")) end -= 1;
  return end > start ? { end, start } : null;
}

/**
 * 範囲に「表示に現れる文字」が空白以外で含まれるか。
 * 記法プレフィックス・括弧・URL のように縮約表示で描画対象を持たない raw 文字は
 * markdownLineDisplaySegments の rawOffsets に現れないため、自然に除外される。
 */
function hasVisibleDisplayText(line: MarkdownSourceLine, range: RawOffsetRange): boolean {
  for (const segment of markdownLineDisplaySegments(line)) {
    for (const [index, rawOffset] of segment.rawOffsets.entries()) {
      if (rawOffset < range.start || rawOffset >= range.end) continue;
      const ch = segment.text[index];
      if (ch !== undefined && !/\s/u.test(ch)) return true;
    }
  }
  return false;
}

/** start の降順で安定ソートする（同じ start の要素は入力順を保つ）。 */
function stableSortByStartDescending(edits: readonly RawEdit[]): readonly RawEdit[] {
  return edits
    .map((edit, index) => ({ edit, index }))
    .sort((a, b) =>
      b.edit.start === a.edit.start ? a.index - b.index : b.edit.start - a.edit.start
    )
    .map((entry) => entry.edit);
}
